import { createSelector } from 'reselect';
import { KEY, KioskState, HardwareTypes, Kiosk } from './types';
import { GlobalState } from 'store/types';

const getKioskState = (state: GlobalState) => state[KEY] as KioskState;

const selectLoading = createSelector(getKioskState, (kiosk) => kiosk.loading);

const selectError = createSelector(getKioskState, (kiosk) => kiosk.error);

const selectTickets = createSelector(getKioskState, (kiosk) => kiosk.tickets);

const selectKiosk = createSelector(getKioskState, (kiosk) => kiosk.kiosk);

const selectKioskVenue = createSelector(selectKiosk, (kiosk) => kiosk && kiosk.venue);

const selectTicketTemplates = createSelector(selectKiosk, (kiosk) => (kiosk && kiosk.ticketTemplates) || []);

const selectKioskVersion = createSelector(getKioskState, (kiosk) => kiosk.version);

const selectKioskWaiting = createSelector(getKioskState, (kiosk) => kiosk.kioskWaiting);

const selectPendingKioskReboot = createSelector(getKioskState, (kiosk) => kiosk.kioskRebootWaiting);

const selectStatusActor = createSelector(getKioskState, (kiosk) => kiosk.statusActor);

const selectInactiveKioskHardwareId = createSelector(getKioskState, (kiosk) => kiosk.inactiveKioskHardwareId);

const findHardware = (type: HardwareTypes) => (kiosk: Kiosk | null) =>
  kiosk && kiosk.hardware && kiosk.hardware.find((hardware) => hardware.type === type);

const selectScannerHardware = createSelector(selectKiosk, findHardware('barcodeScanner'));

const selectPaymentHardware = createSelector(selectKiosk, findHardware('paymentDevice'));

const getAllHardwareByType = (type: HardwareTypes) => (kiosk: Kiosk | null) => {
  if (kiosk && kiosk.hardware) {
    return kiosk.hardware.filter((hardware) => hardware.type === type);
  }
  return [];
};

const selectPrinters = createSelector(selectKiosk, getAllHardwareByType('printer'));

/** Return true if one printer has a currentStock lower than all of its lowStockAlertThresholds */
const selectLowStockThresholdReached = createSelector(selectPrinters, (printers) =>
  printers.some(
    (printer) =>
      !printer.values.currentStock ||
      (printer.configuration.lowStockAlertThresholds &&
        printer.configuration.lowStockAlertThresholds.every(
          (threshold: number) => (printer.values.currentStock as number) <= threshold
        ))
  )
);

export const selectors = {
  selectLoading,
  selectError,
  selectKiosk,
  selectKioskVenue,
  selectTicketTemplates,
  selectTickets,
  selectScannerHardware,
  selectPaymentHardware,
  selectPrinters,
  selectLowStockThresholdReached,
  selectKioskVersion,
  selectKioskWaiting,
  selectStatusActor,
  selectInactiveKioskHardwareId,
  selectPendingKioskReboot
};
